import React, { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, type DragEndEvent } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, GripVertical, KeyRound, Map, Maximize2, Minimize2, MousePointer2, PanelLeftClose, PanelLeftOpen, RefreshCw, Trash2 } from "lucide-react";
import apiClient from "@/api/client";
import { FileDropZone } from "@/components/ui/FileDropZone";
import { Spinner } from "@/components/ui/Spinner";
import { useSignedWmsUrl } from "../hooks/useSignedWmsUrl";

function SortableLayerItem({
  name, layer, isActive, wmsAvailable, onToggle,
}: {
  name: string; layer: { name: string; title: string }; isActive: boolean; wmsAvailable: boolean; onToggle: (n: string) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: name });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 }}
      className="flex items-center gap-1 px-1 py-[3px] hover:bg-gray-50 dark:hover:bg-gray-800 group select-none"
    >
      <span
        {...attributes}
        {...listeners}
        className="flex-shrink-0 text-gray-300 dark:text-gray-600 hover:text-gray-500 cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity touch-none"
      >
        <GripVertical size={12} />
      </span>
      <button
        onClick={() => wmsAvailable && onToggle(name)}
        disabled={!wmsAvailable}
        title={isActive ? "Ukryj" : "Pokaż"}
        className={`flex-shrink-0 w-3 h-3 rounded-sm border-2 transition-all ${
          wmsAvailable
            ? isActive
              ? "bg-[var(--color-primary)] border-[var(--color-primary)]"
              : "border-[var(--color-primary)] bg-transparent"
            : "border-gray-300 bg-transparent"
        }`}
      />
      <span
        onClick={() => wmsAvailable && onToggle(name)}
        title={layer.title}
        className={`text-[11px] break-words flex-1 min-w-0 font-mono leading-tight ${
          wmsAvailable && isActive ? "text-gray-700 dark:text-gray-200 cursor-pointer" : "text-gray-400 dark:text-gray-500"
        }`}
      >
        {layer.title}
      </span>
    </div>
  );
}

interface GISLayer {
  name: string;
  title: string;
  bbox?: [number, number, number, number]; // [west, south, east, north] WGS84
}

interface GISConnection {
  host: string;
  dbname: string;
  user: string;
  has_password: boolean;
}

interface GISProject {
  id: string;
  name: string;
  wms_url: string;
  layers: GISLayer[];
  wms_available: boolean;
  invalid_layers: string[];
  map_center?: {
    lat: number;
    lng: number;
    bounds?: { north: number; south: number; east: number; west: number };
  } | null;
  connections?: GISConnection[];
}

interface TaskGISLink {
  id: string;
  gis_project: GISProject;
}

export function useTaskGISLinks(taskId: string) {
  return useQuery<TaskGISLink[]>({
    queryKey: ["task-gis", taskId],
    queryFn: () =>
      apiClient.get(`/tasks/${taskId}/gis/`).then((r) => r.data.data),
  });
}

function useUploadGIS(taskId: string) {
  const qc = useQueryClient();
  return useMutation<GISProject, Error, File>({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("name", file.name.replace(/\.(qgz|qgs)$/i, ""));
      const project = await apiClient
        .post("/gis/projects/", fd, { headers: { "Content-Type": "multipart/form-data" } })
        .then((r) => r.data.data as GISProject);
      await apiClient.post(`/tasks/${taskId}/gis/`, { gis_project: project.id });
      return project;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["task-gis", taskId] }),
  });
}

function useRefreshGISProject(taskId: string) {
  const qc = useQueryClient();
  return useMutation<GISProject, Error, { projectId: string; credentials?: object[] }>({
    mutationFn: ({ projectId, credentials }) =>
      apiClient
        .patch(`/gis/projects/${projectId}/`, credentials ? { credentials } : {})
        .then((r) => r.data.data as GISProject),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["task-gis", taskId] }),
  });
}

function useUnlinkGIS(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (linkId: string) => apiClient.delete(`/tasks/${taskId}/gis/${linkId}/`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["task-gis", taskId] }),
  });
}

function buildFeatureHtml(raw: string): string {
  // Strip html/head/body wrappers that QGIS Server may include
  let body = raw
    .replace(/<html[\s\S]*?<body[^>]*>/i, "")
    .replace(/<\/body>[\s\S]*$/i, "")
    .trim();

  // Extract layer name from <h3>/<h4>/<caption>, or first colspan <tr>
  let layerName = "";
  body = body
    .replace(/<h[1-6][^>]*>([\s\S]*?)<\/h[1-6]>/i, (_, t) => {
      layerName = t.replace(/<[^>]+>/g, "").trim();
      return "";
    })
    .replace(/<caption[^>]*>([\s\S]*?)<\/caption>/i, (_, t) => {
      if (!layerName) layerName = t.replace(/<[^>]+>/g, "").trim();
      return "";
    });

  if (!layerName) {
    // Older QGIS: first <tr> contains a single <td colspan="2">Layer name</td>
    body = body.replace(
      /(<table[^>]*>)\s*<tr[^>]*>\s*<td[^>]*colspan[^>]*>([\s\S]*?)<\/td>\s*<\/tr>/i,
      (_, tableOpen, inner) => {
        layerName = inner.replace(/<[^>]+>/g, "").trim();
        return tableOpen;
      },
    );
  }

  // Annotate typed values only inside table cells to avoid false positives
  body = body
    .replace(/(<t[dh][^>]*>)\s*NULL\s*(<\/t[dh]>)/g, '$1<span class="elg-fp-null">NULL</span>$2')
    .replace(/(<t[dh][^>]*>)\s*true\s*(<\/t[dh]>)/g, '$1<span class="elg-fp-true">true</span>$2')
    .replace(/(<t[dh][^>]*>)\s*false\s*(<\/t[dh]>)/g, '$1<span class="elg-fp-false">false</span>$2');

  const header = layerName ? `<div class="elg-fp-hdr">${layerName}</div>` : "";
  return `${header}<div class="elg-fp">${body}</div>`;
}

// ── Fullscreen WMS map ────────────────────────────────────────────────────────

function LeafletWMSMap({ projectId, layers, wmsAvailable, invalidLayers, mapCenter }: { projectId: string; layers: GISLayer[]; wmsAvailable: boolean; invalidLayers: string[]; mapCenter?: { lat: number; lng: number; bounds?: { north: number; south: number; east: number; west: number } } | null }) {
  // Pobiera świeży podpisany WMS URL z backendu (HMAC + 1h TTL, auto-refresh co 50 min).
  // Patrz: hooks/useSignedWmsUrl.ts dla pełnego wyjaśnienia.
  // Bez ważnego signed URL nginx zwraca 403 — czyli musimy poczekać na hook
  // przed mount mapy (inaczej wszystkie tile requests dostają 403).
  const { data: signed, isLoading: signLoading, error: signError } = useSignedWmsUrl(projectId);
  const wmsUrl = signed?.wms_url ?? "";
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<L.Map | null>(null);
  const wmsRef = useRef<L.TileLayer.WMS | null>(null);
  const wmsDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [layerOrder, setLayerOrder] = useState<string[]>(() => layers.map((l) => l.name));
  const [active, setActive] = useState<Set<string>>(() => new Set(layers.map((l) => l.name)));
  const [fullscreen, setFullscreen] = useState(false);
  const [identify, setIdentify] = useState(false);
  const [tilesLoading, setTilesLoading] = useState(false);
  // Mobile (<640px) startuje z zamkniętym panelem — w sidebar w-48 zajmowałby
  // ~60% szerokości panelu, mapa byłaby wąskim paskiem.
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(() => {
    if (typeof window === "undefined") return true;
    return window.innerWidth >= 640;
  });

  const activeRef = useRef(active);
  const layerOrderRef = useRef(layerOrder);
  const identifyRef = useRef(identify);
  useEffect(() => { activeRef.current = active; }, [active]);
  useEffect(() => { layerOrderRef.current = layerOrder; }, [layerOrder]);
  useEffect(() => { identifyRef.current = identify; }, [identify]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  function handleDragEnd(event: DragEndEvent) {
    const { active: a, over } = event;
    if (over && a.id !== over.id) {
      setLayerOrder((prev) => arrayMove(prev, prev.indexOf(a.id as string), prev.indexOf(over.id as string)));
    }
  }

  useEffect(() => {
    if (!mapInstance.current) return;
    mapInstance.current.getContainer().style.cursor = identify ? "crosshair" : "";
  }, [identify]);

  useEffect(() => {
    if (!mapRef.current || mapInstance.current) return;
    // Czekamy aż signed WMS URL przyjdzie — bez niego nginx zwraca 403 dla
    // każdego tile request. Hook re-trigger'uje useEffect gdy `wmsUrl` się
    // ustawi (po pierwszym fetchu z /api/v1/gis/projects/<id>/wms-url/).
    if (!wmsUrl) return;
    const map = L.map(mapRef.current, { preferCanvas: true, zoomControl: false, attributionControl: false });
    L.control.zoom({ position: "topright" }).addTo(map);

    // Priority:
    //  1. Saved QGIS view (mapcanvas extent → WGS84 bounds) — honors zoom + center
    //     set by the user in QGIS Desktop. This is the most reliable signal.
    //  2. fitBounds over layer bboxes — skip world-spanning (OSM, country-wide
    //     overlays like "Sieć telekomunikacyjna" pokrywające całą Polskę) by
    //     dropping any layer >2x larger than the median layer area.
    //  3. setView(mapCenter, 14) — only center, fixed zoom.
    //  4. Fallback: cała Polska.
    if (mapCenter?.bounds) {
      const b = mapCenter.bounds;
      map.fitBounds([[b.south, b.west], [b.north, b.east]], { padding: [20, 20] });
    } else {
      const bboxed = layers
        .filter((l) => l.bbox)
        .map((l) => {
          const [w, s, e, n] = l.bbox!;
          return { l, area: (e - w) * (n - s) };
        })
        .filter((x) => x.area > 0 && x.area < 5000);
      // Drop outliers: layers covering >2× the median area (Polish-wide overlays
      // when most other layers are local — they were inflating the union bbox).
      let kept = bboxed;
      if (bboxed.length >= 3) {
        const sorted = [...bboxed].map((x) => x.area).sort((a, b) => a - b);
        const median = sorted[Math.floor(sorted.length / 2)];
        kept = bboxed.filter((x) => x.area <= median * 2);
      }
      if (kept.length > 0) {
        const west = Math.min(...kept.map((x) => x.l.bbox![0]));
        const south = Math.min(...kept.map((x) => x.l.bbox![1]));
        const east = Math.max(...kept.map((x) => x.l.bbox![2]));
        const north = Math.max(...kept.map((x) => x.l.bbox![3]));
        map.fitBounds([[south, west], [north, east]], { padding: [20, 20] });
      } else if (mapCenter) {
        map.setView([mapCenter.lat, mapCenter.lng], 14);
      } else {
        map.setView([52.0, 19.4], 6);
      }
    }

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 21,
    }).addTo(map);

    map.on("click", async (e: L.LeafletMouseEvent) => {
      if (!identifyRef.current) return;
      const activeList = layerOrderRef.current.filter((n) => activeRef.current.has(n));
      if (activeList.length === 0) return;
      // CRS:84 = WGS84 lon/lat — bbox in degrees matches getBounds() directly
      const bounds = map.getBounds();
      const size = map.getSize();
      const pt = map.latLngToContainerPoint(e.latlng);
      const bbox = `${bounds.getWest()},${bounds.getSouth()},${bounds.getEast()},${bounds.getNorth()}`;
      const queryLayers = activeList.join(",");
      const url =
        `${wmsUrl}&SERVICE=WMS&VERSION=1.3.0&REQUEST=GetFeatureInfo` +
        `&LAYERS=${queryLayers}&QUERY_LAYERS=${queryLayers}` +
        `&INFO_FORMAT=text/html&CRS=CRS:84` +
        `&BBOX=${bbox}&WIDTH=${size.x}&HEIGHT=${size.y}` +
        `&I=${Math.round(pt.x)}&J=${Math.round(pt.y)}&FEATURE_COUNT=10`;
      try {
        const resp = await fetch(url);
        const html = await resp.text();
        if (html && html.includes("<table")) {
          L.popup({ maxWidth: 500, className: "elg-popup" })
            .setLatLng(e.latlng)
            .setContent(buildFeatureHtml(html))
            .openOn(map);
        }
      } catch { /* silent */ }
    });

    mapInstance.current = map;
    // ŚWIADOMIE BEZ cleanup function — gdy `wmsUrl` się zmieni (refresh signed
    // URL co 50 min), useEffect re-runs ALE `if (mapInstance.current) return`
    // wczesny pomija re-init mapy. Bez return cleanup, refresh URL NIE
    // unmountuje mapy → zachowany zoom/pan state usera.
    // Cleanup całej mapy jest w osobnym useEffect poniżej (deps []).
    // Aktualizacja WMS layer URL po refresh zachodzi w drugim useEffect
    // (deps [..., wmsUrl]) bez re-init mapy.
  }, [wmsUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup mapy TYLKO przy unmount całego komponentu (nie przy zmianie wmsUrl).
  useEffect(() => {
    return () => {
      if (wmsDebounceRef.current) clearTimeout(wmsDebounceRef.current);
      if (mapInstance.current) {
        mapInstance.current.remove();
        mapInstance.current = null;
        wmsRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!wmsAvailable || !mapInstance.current) return;
    const map = mapInstance.current;
    const activeLayers = [...layerOrder].reverse().filter((n) => active.has(n));
    if (wmsRef.current) {
      if (activeLayers.length === 0) { map.removeLayer(wmsRef.current); wmsRef.current = null; }
      else { wmsRef.current.setParams({ layers: activeLayers.join(",") } as L.WMSParams); }
    } else if (activeLayers.length > 0) {
      const wms = L.tileLayer.wms(wmsUrl, {
        layers: activeLayers.join(","), format: "image/png", transparent: true,
        version: "1.3.0", tileSize: 512, zIndex: 2, maxZoom: 21,
        updateWhenIdle: true, updateWhenZooming: false, keepBuffer: 1,
      });
      // Load tiles only after 300ms of no movement
      const _origUpdate = (wms as any)._update.bind(wms);
      (wms as any)._update = () => {
        if (wmsDebounceRef.current) clearTimeout(wmsDebounceRef.current);
        wmsDebounceRef.current = setTimeout(_origUpdate, 200);
      };
      map.on("movestart zoomstart", () => {
        if (wmsDebounceRef.current) { clearTimeout(wmsDebounceRef.current); wmsDebounceRef.current = null; }
      });
      wms.on("loading", () => setTilesLoading(true));
      wms.on("load", () => setTilesLoading(false));
      setTilesLoading(true);
      wms.addTo(map);
      wmsRef.current = wms;
    }
  }, [active, layerOrder, wmsAvailable, wmsUrl]);

  useEffect(() => { setTimeout(() => mapInstance.current?.invalidateSize(), 50); }, [fullscreen, sidebarOpen]);

  function toggle(name: string) {
    setActive((s) => { const n = new Set(s); n.has(name) ? n.delete(name) : n.add(name); return n; });
  }

  // Fullscreen: z-index 2000 covers Leaflet's max z-index (1000 for attribution/popups)
  const popupCloseButtonCss = `
    .elg-popup .leaflet-popup-close-button {
      background: var(--color-primary) !important;
      color: #fff !important;
      border-radius: 0 !important;
      width: 24px !important;
      height: 24px !important;
      top: 5px !important;
      right: 5px !important;
      font-size: 18px !important;
      line-height: 24px !important;
      text-align: center !important;
      padding: 0 !important;
      opacity: 1 !important;
      z-index: 10 !important;
      box-shadow: 0 1px 4px rgba(0,0,0,.25) !important;
    }
    .elg-popup .leaflet-popup-close-button:hover {
      background: var(--color-primary) !important;
      color: #fff !important;
      opacity: 0.85 !important;
    }
  `;

  const containerStyle: React.CSSProperties = fullscreen
    ? { position: "fixed", inset: 0, zIndex: 2000, height: "100dvh" }
    : { height: 500 };

  return (
    <div
      className={fullscreen ? "" : "relative rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700"}
      style={containerStyle}
    >
      <style>{popupCloseButtonCss}</style>
      <div ref={mapRef} className="absolute inset-0" />

      {tilesLoading && (
        <div className="absolute inset-0 z-[450] flex items-center justify-center pointer-events-none">
          <div className="bg-white/80 dark:bg-gray-900/80 p-3 shadow">
            <Spinner size="lg" />
          </div>
        </div>
      )}

      {/* Spinner gdy hook pobiera pierwszy signed URL (sekunda, max) */}
      {signLoading && !wmsUrl && (
        <div className="absolute inset-0 z-[460] flex items-center justify-center bg-gray-50 dark:bg-gray-900">
          <Spinner size="lg" />
        </div>
      )}

      {/* Komunikat gdy nie udało się pobrać signed URL (auth fail, network, brak permission) */}
      {signError && !wmsUrl && (
        <div className="absolute inset-0 z-[460] flex items-center justify-center bg-gray-50 dark:bg-gray-900">
          <div className="text-sm text-red-600 dark:text-red-400 max-w-md text-center p-4">
            Nie udało się pobrać podpisanego URL dla mapy.<br/>
            <span className="text-xs text-gray-500 dark:text-gray-400">Sprawdź uprawnienia (Internal user wymagany) lub odśwież stronę.</span>
          </div>
        </div>
      )}

      {/* Floating button to reopen left panel when collapsed */}
      {!sidebarOpen && (
        <button
          onClick={() => setSidebarOpen(true)}
          title="Pokaż panel warstw"
          className="absolute left-2 top-2 z-[400] p-1.5 rounded bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow text-gray-500 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
        >
          <PanelLeftOpen size={14} />
        </button>
      )}

      {/* QGIS-style left panel */}
      {sidebarOpen && (
      <div className="absolute left-0 top-0 bottom-0 w-48 max-w-[70%] z-[400] flex flex-col bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 shadow">
        <div className="px-2 py-1.5 border-b border-gray-100 dark:border-gray-800 flex-shrink-0 flex items-center justify-between gap-1">
          <span className="text-[10px] font-bold uppercase tracking-wide text-gray-400 dark:text-gray-500 font-mono">
            Warstwy
          </span>
          <button
            onClick={() => setSidebarOpen(false)}
            title="Ukryj panel warstw"
            className="p-0.5 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <PanelLeftClose size={12} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto py-0.5 min-h-0">
          {layers.length === 0 && <p className="px-2 py-2 text-xs text-gray-400 italic font-mono">Brak warstw</p>}
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={layerOrder} strategy={verticalListSortingStrategy}>
              {layerOrder.map((name) => {
                const layer = layers.find((l) => l.name === name);
                if (!layer) return null;
                return (
                  <SortableLayerItem
                    key={name}
                    name={name}
                    layer={layer}
                    isActive={active.has(name)}
                    wmsAvailable={wmsAvailable}
                    onToggle={toggle}
                  />
                );
              })}
            </SortableContext>
          </DndContext>
        </div>
        <div className="flex-shrink-0 border-t border-gray-100 dark:border-gray-800 px-1.5 py-1 flex items-center gap-1">
          {wmsAvailable && (
            <button
              onClick={() => setIdentify((v) => !v)}
              title={identify ? "Wyłącz identyfikację" : "Identyfikuj obiekty"}
              className={`p-1 rounded transition-colors ${identify ? "bg-[var(--color-primary)] text-white" : "text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"}`}
            >
              <MousePointer2 size={13} />
            </button>
          )}
          <button
            onClick={() => setFullscreen((f) => !f)}
            title={fullscreen ? "Zamknij pełny ekran" : "Pełny ekran"}
            className="p-1 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 ml-auto"
          >
            {fullscreen ? <Minimize2 size={13} /> : <Maximize2 size={13} />}
          </button>
        </div>
      </div>
      )}

      {(!wmsAvailable || invalidLayers.length > 0) && (
        <div className="absolute top-2 right-2 z-[400] max-w-xs rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-950/90 dark:border-amber-800 px-3 py-2 text-xs text-amber-700 dark:text-amber-400 shadow-sm">
          <div className="flex items-start gap-1.5">
            <AlertTriangle size={12} className="flex-shrink-0 mt-0.5" />
            <div>
              {!wmsAvailable
                ? "QGIS Server nie mógł załadować projektu — mapa WMS niedostępna."
                : `Pominięte przez QGIS Server (${invalidLayers.length}):`}
              {wmsAvailable && invalidLayers.length > 0 && (
                <ul className="mt-1 space-y-0.5 font-mono text-[10px]">
                  {invalidLayers.map((n) => <li key={n} className="truncate">• {n}</li>)}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
      {identify && (
        <div className="absolute bottom-6 right-2 z-[400] rounded bg-[var(--color-primary)] text-white text-[10px] px-2 py-0.5 shadow pointer-events-none">
          Kliknij na obiekt
        </div>
      )}
    </div>
  );
}

// ── Credential form ───────────────────────────────────────────────────────────

function CredentialForm({
  project,
  onSave,
  isSaving,
}: {
  project: GISProject;
  onSave: (credentials: object[]) => void;
  isSaving: boolean;
}) {
  const connections = project.connections ?? [];
  const connKey = (c: GISConnection) => `${c.host}|${c.dbname}`;
  const [users, setUsers] = useState<Record<string, string>>(() =>
    Object.fromEntries(connections.map((c) => [connKey(c), c.user]))
  );
  const [passwords, setPasswords] = useState<Record<string, string>>({});
  const [sslRequired, setSslRequired] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(connections.map((c) => [connKey(c), true]))
  );

  // Show form when WMS fails (need to fix credentials) or when any connection lacks a password
  const needsForm = !project.wms_available || connections.some((c) => !c.has_password);
  if (!needsForm || connections.length === 0) return null;

  const inputCls = "text-xs border border-gray-300 dark:border-gray-600 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] bg-white dark:bg-gray-800 w-full";

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 p-3 flex flex-col gap-3">
      <div className="flex items-center gap-1.5 text-xs font-medium text-amber-700 dark:text-amber-400">
        <KeyRound size={13} />
        Dane logowania do bazy PostgreSQL
        <span className="ml-1 text-[10px] font-normal bg-amber-200 dark:bg-amber-800 text-amber-700 dark:text-amber-300 px-1.5 py-0.5 rounded">
          eksperymentalne — może nie działać
        </span>
      </div>
      {connections.map((c) => {
        const k = connKey(c);
        return (
          <div key={k} className="flex flex-col gap-1.5">
            <p className="text-[10px] text-gray-400 font-mono">{c.host}/{c.dbname}</p>
            <div className="grid grid-cols-2 gap-1.5">
              <input
                placeholder="Użytkownik"
                value={users[k] ?? ""}
                onChange={(e) => setUsers((p) => ({ ...p, [k]: e.target.value }))}
                className={inputCls}
              />
              <input
                type="password"
                placeholder="Hasło"
                value={passwords[k] ?? ""}
                onChange={(e) => setPasswords((p) => ({ ...p, [k]: e.target.value }))}
                className={inputCls}
              />
            </div>
            <label className="flex items-center gap-1.5 text-[11px] text-gray-600 dark:text-gray-400 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={sslRequired[k] ?? true}
                onChange={(e) => setSslRequired((p) => ({ ...p, [k]: e.target.checked }))}
                className="w-3 h-3"
              />
              SSL required (sslmode=require)
            </label>
          </div>
        );
      })}
      <button
        disabled={isSaving || connections.some((c) => !passwords[connKey(c)] || !users[connKey(c)])}
        onClick={() =>
          onSave(
            connections.map((c) => ({
              host: c.host,
              dbname: c.dbname,
              user: users[connKey(c)] ?? c.user,
              password: passwords[connKey(c)] ?? "",
              sslmode: sslRequired[connKey(c)] ? "require" : "disable",
            }))
          )
        }
        className="self-start text-xs px-3 py-1.5 bg-[var(--color-primary)] text-white rounded-md disabled:opacity-40 hover:opacity-90 transition-opacity"
      >
        {isSaving ? "Zapisywanie…" : "Zapisz i załaduj warstwy"}
      </button>
    </div>
  );
}

// ── Upload drop zone ──────────────────────────────────────────────────────────

function UploadZone({ onUpload, isPending }: { onUpload: (f: File) => void; isPending: boolean }) {
  return (
    <FileDropZone
      accept=".qgz,.qgs"
      disabled={isPending}
      label={isPending ? "Wgrywanie…" : "Dodaj plik .qgz / .qgs"}
      onSelect={(fs) => {
        const f = fs[0];
        if (f && /\.(qgz|qgs)$/i.test(f.name)) onUpload(f);
      }}
    />
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function TaskMapTab({ taskId }: { taskId: string }) {
  const { data: links = [], isLoading } = useTaskGISLinks(taskId);
  const { mutate: unlink, isPending: unlinking } = useUnlinkGIS(taskId);
  const { mutateAsync: upload, isPending: uploading } = useUploadGIS(taskId);
  const { mutate: refresh, isPending: refreshing, variables: refreshVars } = useRefreshGISProject(taskId);

  const [credProjects, setCredProjects] = useState<Record<string, GISProject>>({});

  async function handleUpload(file: File) {
    const project = await upload(file);
    const needsCreds = project.connections?.some((c) => !c.has_password);
    if (needsCreds) {
      setCredProjects((prev) => ({ ...prev, [project.id]: project }));
    }
  }

  function handleSaveCredentials(projectId: string, credentials: object[]) {
    refresh(
      { projectId, credentials },
      {
        onSuccess: (updated) => {
          const stillMissing = updated.connections?.some((c) => !c.has_password);
          if (!stillMissing) {
            setCredProjects((prev) => { const next = { ...prev }; delete next[projectId]; return next; });
          } else {
            setCredProjects((prev) => ({ ...prev, [projectId]: updated }));
          }
        },
      }
    );
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {links.length === 0 && !uploading && (
        <UploadZone onUpload={handleUpload} isPending={uploading} />
      )}
      {uploading && (
        <div className="flex justify-center py-8">
          <Spinner size="md" />
        </div>
      )}

      {links.map((link) => {
        const project = link.gis_project;
        const credProject = credProjects[project.id];
        const isRefreshing = refreshing && refreshVars?.projectId === project.id;

        return (
          <div key={link.id} className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-gray-200">
                <Map size={14} className="text-[var(--color-primary)]" />
                {project.name}
                <span className="text-xs font-normal text-gray-400">
                  ({project.layers.length} {project.layers.length === 1 ? "warstwa" : "warstw"})
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => refresh({ projectId: project.id })}
                  disabled={isRefreshing}
                  title="Odśwież warstwy z QGIS Server"
                  className="text-xs text-gray-400 hover:text-[var(--color-primary)] transition-colors disabled:opacity-40"
                >
                  <RefreshCw size={13} className={isRefreshing ? "animate-spin" : ""} />
                </button>
                <button
                  onClick={() => unlink(link.id)}
                  disabled={unlinking}
                  title="Odepnij projekt GIS"
                  className="text-xs text-gray-300 hover:text-red-500 transition-colors disabled:opacity-40"
                >
                  <Trash2 size={13} />
                </button>
              </div>
            </div>

            {credProject && (
              <CredentialForm
                project={credProject}
                isSaving={isRefreshing}
                onSave={(creds) => handleSaveCredentials(project.id, creds)}
              />
            )}

            {/* projectId zamiast wms_url — LeafletWMSMap sama wewnętrznie
                pobiera świeży signed URL z /api/v1/gis/projects/<id>/wms-url/
                przez hook useSignedWmsUrl (auto-refresh co 50 min). */}
            <LeafletWMSMap projectId={project.id} layers={project.layers} wmsAvailable={project.wms_available} invalidLayers={project.invalid_layers ?? []} mapCenter={project.map_center} />
          </div>
        );
      })}

      {links.length > 0 && (
        <UploadZone onUpload={handleUpload} isPending={uploading} />
      )}
    </div>
  );
}
